import { useEffect, useState } from "react";
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, Boxes, Copy, FileSpreadsheet, Pencil, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/components";
import { resetElementCache } from "@/lib/element";
import { CategoryDefaults } from "@/components/settings/CategoryDefaults";
import { LineEditor, EMPTY_LINE } from "@/components/settings/LineEditor";
import { SupplierImportDialog } from "@/components/settings/SupplierImportDialog";
import { confirmSheet, promptSheet } from "@/components/ui/action-sheet";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const btn = "inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-200";

export function NotUsed({ on, children, testId }) {
  if (!on) return children;
  return (
    <div data-testid={testId} className="relative">
      <p className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-200">Not used: pricing comes from your supplier list</p>
      <div className="pointer-events-none select-none opacity-50" aria-disabled="true">{children}</div>
    </div>
  );
}

function TiltUplift({ company, onCompany }) {
  const save = async (v) => {
    const pct = Math.min(100, Math.max(0, Number(v) || 0));
    try {
      await api.put("/settings/tilt-turn", { pct });
      onCompany({ ...company, tilt_turn_uplift_pct: pct });
      toast.success("Tilt-Turn uplift saved");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <label className="mt-3 flex items-center gap-3 rounded-xl border border-slate-800 p-3 text-sm text-slate-100">
      Tilt-Turn uplift
      <input key={company?.tilt_turn_uplift_pct ?? 15} data-testid="tilt-turn-uplift-input" defaultValue={company?.tilt_turn_uplift_pct ?? 15} onBlur={(e) => save(e.target.value)}
        inputMode="decimal" className="h-8 w-24 rounded border border-slate-800 bg-white px-2 text-right font-mono-num text-sm outline-none focus:border-amber-500/70" />
      <span className="text-xs text-slate-400">% added to the AW88 Casement cost for Tilt-Turn products and designer sections</span>
    </label>
  );
}

const RATING_KEYS = ["casement", "fixed", "sliding_door", "swing_door", "sliding_window", "hung"];

function ElementSettings() {
  const [el, setEl] = useState(null);
  useEffect(() => { api.get("/element").then((r) => setEl(r.data)).catch(() => {}); }, []);
  if (!el) return null;
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const setR = (k, f, v) => setEl({ ...el, ratings: { ...el.ratings, [k]: { ...el.ratings[k], [f]: v } } });
  const save = async () => {
    try {
      const ratings = Object.fromEntries(RATING_KEYS.map((k) => [k, { ...el.ratings[k], u: num(el.ratings[k]?.u), shgc: num(el.ratings[k]?.shgc) }]));
      const { data } = await api.put("/element", { pct: Number(el.pct) || 0, glass: el.glass, ratings });
      setEl(data);
      resetElementCache(data);
      toast.success("Element series saved. Lines already on Element keep their saved %.");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const cell = "h-8 w-20 rounded border border-slate-800 bg-white px-2 text-right font-mono-num text-sm outline-none focus:border-amber-500/70";
  return (
    <div className="mt-3 space-y-3 rounded-xl border border-slate-800 p-4 text-sm text-slate-100" data-testid="element-settings">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold">Element series</span>
        <input data-testid="element-pct-input" value={el.pct} onChange={(e) => setEl({ ...el, pct: e.target.value })} inputMode="decimal" className={cell} />
        <span className="text-xs text-slate-400">% added to the cost (after Tilt-Turn, before minimum and multiplier)</span>
      </div>
      <label className="block text-xs text-slate-400">Element glass package
        <input data-testid="element-glass-input" value={el.glass} onChange={(e) => setEl({ ...el, glass: e.target.value })} className="mt-1 h-9 w-full rounded border border-slate-800 bg-white px-2 text-sm text-slate-100 outline-none focus:border-amber-500/70" />
      </label>
      <table className="w-full text-xs" data-testid="element-ratings-table">
        <thead><tr className="text-left uppercase tracking-wider text-slate-500"><th className="py-1">Category</th><th>Model</th><th className="text-right">U-factor</th><th className="text-right">SHGC</th></tr></thead>
        <tbody>{RATING_KEYS.map((k) => (
          <tr key={k} className="border-t border-slate-800/60">
            <td className="py-1.5 text-slate-300">{el.ratings[k]?.label || k}</td>
            <td><input data-testid={`element-model-${k}`} value={el.ratings[k]?.model || ""} onChange={(e) => setR(k, "model", e.target.value)} className="h-8 w-full rounded border border-slate-800 bg-white px-2 text-xs outline-none" placeholder="Not listed" /></td>
            <td className="text-right"><input data-testid={`element-u-${k}`} value={el.ratings[k]?.u ?? ""} onChange={(e) => setR(k, "u", e.target.value)} inputMode="decimal" className={cell} placeholder="—" /></td>
            <td className="text-right"><input data-testid={`element-shgc-${k}`} value={el.ratings[k]?.shgc ?? ""} onChange={(e) => setR(k, "shgc", e.target.value)} inputMode="decimal" className={cell} placeholder="—" /></td>
          </tr>))}
        </tbody>
      </table>
      <p className="text-xs text-slate-500">Blank U/SHGC = "Element rating not listed" (the line keeps its own values). Source: report VLX-100526.</p>
      <button data-testid="element-save-button" onClick={save} className="rounded-full bg-amber-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-600">Save Element settings</button>
    </div>
  );
}

export function ProductLinesSection({ types, onTypesChanged, company, onCompany }) {
  const [lines, setLines] = useState(null);
  const [edit, setEdit] = useState(null);
  const [importing, setImporting] = useState(false);
  const load = () => api.get("/settings/product-lines").then((r) => setLines(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const typeName = (id) => types.find((t) => t.id === id)?.name || "Not mapped";
  const [mapping, setMapping] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = () => { load(); onTypesChanged(); setVersion((v) => v + 1); };
  const autoMap = async () => {
    setMapping(true);
    try {
      const { data } = await api.post("/settings/product-lines/auto-map");
      toast.success(`Auto-mapped ${data.mapped} product lines${data.screens ? ` · ${data.screens} screens set up as add-ons` : ""}${data.not_mapped ? ` · ${data.not_mapped} have no matching type` : ""}`);
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setMapping(false);
    }
  };
  const del = async (ln) => {
    if (!(await confirmSheet({ title: `Delete product line "${ln.name}"?`, message: "Saved quotes keep their prices.", confirm: "Delete Product Line", destructive: true }))) return;
    try {
      await api.delete(`/settings/product-lines/${ln.id}`);
      toast.success("Product line deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
      if (e?.response?.status === 409 && (await confirmSheet({ title: "Archive it instead?", message: "It stays on saved quotes but can't be picked.", confirm: "Archive" }))) arch(ln, true);
    }
  };
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const dup = async (ln) => {
    const name = await promptSheet({ title: "Name for the copy", input: `${ln.name} (copy)`, confirm: "Duplicate", required: true });
    if (name) run(() => api.post(`/settings/product-lines/${ln.id}/duplicate`, { name }), `Duplicated as ${name}`);
  };
  const arch = (ln, on) => run(() => api.put(`/settings/product-lines/${ln.id}/archive`, { archived: on }), on ? "Archived" : "Restored");
  const move = (i, d) => {
    const ids = lines.map((x) => x.id);
    if (i + d < 0 || i + d >= ids.length) return;
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    run(() => api.put("/settings/product-lines/order", { ids }), "Order saved");
  };
  const actions = (ln, i) => (
    <td className="whitespace-nowrap px-2 text-right">
      <button aria-label="Move up" data-testid={`product-line-up-${i}`} onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-amber-200"><ArrowUp className="h-3.5 w-3.5" /></button>
      <button aria-label="Move down" data-testid={`product-line-down-${i}`} onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-amber-200"><ArrowDown className="h-3.5 w-3.5" /></button>
      <button aria-label="Edit" data-testid={`product-line-edit-${i}`} onClick={() => setEdit(ln)} className="p-1 text-slate-400 hover:text-amber-200"><Pencil className="h-4 w-4" /></button>
      <button aria-label="Duplicate" data-testid={`product-line-duplicate-${i}`} onClick={() => dup(ln)} className="p-1 text-slate-400 hover:text-amber-200"><Copy className="h-4 w-4" /></button>
      <button aria-label={ln.archived ? "Restore" : "Archive"} data-testid={`product-line-archive-${i}`} onClick={() => arch(ln, !ln.archived)} className="p-1 text-slate-400 hover:text-amber-200">
        {ln.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
      <button aria-label="Delete" data-testid={`product-line-delete-${i}`} onClick={() => del(ln)} className="p-1 text-slate-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
    </td>
  );
  return (
    <section data-testid="product-lines-section" className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-100"><Boxes className="h-4 w-4 text-amber-300" />
            <h2 className="font-display text-base font-bold md:text-lg">Component pricing · product lines</h2></div>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">Supplier cost = frame + opening sashes + glass + crate + extras, all in $/sq ft. Your markup is applied on top.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="product-lines-auto-map-button" onClick={autoMap} disabled={mapping} className={btn}><Wand2 className="h-4 w-4" /> Auto-map all</button>
          <button data-testid="product-lines-import-button" onClick={() => setImporting(true)} className={btn}><FileSpreadsheet className="h-4 w-4" /> Import supplier list</button>
          <button data-testid="product-lines-add-button" onClick={() => setEdit(EMPTY_LINE)} className={btn}><Plus className="h-4 w-4" /> Add line</button>
        </div>
      </div>
      <TiltUplift company={company} onCompany={onCompany} />
      <ElementSettings />
      {company?.supplier_only && lines && (
        <p data-testid="supplier-only-unmapped-types" className="mt-2 text-xs text-slate-400">
          {(() => {
            const missing = types.filter((t) => !lines.some((l) => l.type_id === t.id)).map((t) => t.name);
            return missing.length ? `Types with no product line (lines show "No supplier price" until you pick one): ${missing.join(", ")}` : "Every type has a product line.";
          })()}
        </p>
      )}
      <div className="mt-5 overflow-x-auto">
        {lines && lines.length === 0 && <p data-testid="product-lines-empty" className="text-sm text-slate-500">No product lines yet. Import your supplier list or add one.</p>}
        {lines && lines.length > 0 && (
          <table className="w-full text-left text-sm" data-testid="product-lines-table">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
              <tr><th className="px-2 py-1">Line</th><th className="px-2">Category</th><th className="px-2">Type</th><th className="px-2 text-right">Frame</th><th className="px-2 text-right">Sash std / over</th>
                <th className="px-2 text-right">Std sash limit</th><th className="px-2 text-right">Crate</th><th /></tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => ln.category === "screen" ? (
                <tr key={ln.id} data-testid={`product-line-row-${i}`} className="border-t border-slate-800/70 hover:bg-slate-900/40">
                  <td className="px-2 py-2 text-slate-100">{ln.name}</td>
                  <td data-testid={`product-line-category-${i}`} className="px-2 text-slate-400">Screen</td>
                  <td data-testid={`product-line-type-${i}`} className="px-2 text-xs text-emerald-300">
                    <span className="font-semibold">Add-on</span> · applies to {(ln.applies_to || []).map((c) => CATEGORY_LABELS[c]).join(", ") || "nothing yet"}</td>
                  <td className="px-2 text-right font-mono-num text-slate-300">{money(ln.frame_rate)}<span className="block text-[10px] text-slate-500">screen /sq ft</span></td>
                  <td data-testid={`product-line-screen-min-${i}`} className="px-2 text-right font-mono-num text-xs text-slate-300">
                    min {Number(ln.min_area || 0).toFixed(2)}{ln.min_area_double ? ` / ${Number(ln.min_area_double).toFixed(2)} dbl` : ""} sq ft</td>
                  <td className="px-2 text-right font-mono-num text-xs text-slate-300">{ln.valid_max ? `range ${Number(ln.valid_min).toFixed(2)}–${Number(ln.valid_max).toFixed(2)} sq ft` : "—"}</td>
                  <td className="px-2 text-right text-slate-500">—</td>
                  {actions(ln, i)}
                </tr>
              ) : (
                <tr key={ln.id} data-testid={`product-line-row-${i}`} className={`border-t border-slate-800/70 hover:bg-slate-900/40 ${ln.archived ? "opacity-50" : ""}`}>
                  <td className="px-2 py-2 text-slate-100">{ln.name}
                    {ln.estimated && <span data-testid={`product-line-estimated-${i}`} className="ml-2 rounded bg-orange-500/15 px-1.5 text-[10px] font-semibold text-orange-300">Estimated — confirm with supplier</span>}
                    {ln.archived && <span className="ml-2 rounded bg-slate-800 px-1.5 text-[10px] uppercase text-slate-400">Archived</span>}</td>
                  <td data-testid={`product-line-category-${i}`} className="px-2 text-slate-400">{CATEGORY_LABELS[ln.category] || "—"}</td>
                  <td data-testid={`product-line-type-${i}`} className={`px-2 ${ln.type_id ? "text-slate-400" : "font-semibold text-red-500"}`}>{typeName(ln.type_id)}</td>
                  <td className="px-2 text-right font-mono-num text-slate-300">{ln.kind === "slider" ? "track" : money(ln.frame_rate)}</td>
                  <td className="px-2 text-right font-mono-num text-slate-300">{ln.kind === "bifold" ? (
                    <span data-testid={`product-line-bifold-rates-${i}`} className="whitespace-nowrap text-[11px]">Hinge {money(ln.hinge_rate)} · Lock {money(ln.lock_rate)} · Smart {ln.smart_lock_rate == null ? <span className="text-orange-300">not set</span> : money(ln.smart_lock_rate)}</span>
                  ) : ln.kind === "slider" ? "—" : `${money(ln.sash_std_rate)} / ${money(ln.sash_over_rate)}`}</td>
                  <td className="px-2 text-right font-mono-num text-slate-300">{ln.kind === "slider" || !ln.sash_std_rate ? "—" : `W ≤ ${ln.sash_max_w || (ln.category === "awning" ? 59.02 : 33.43)}" · H ≤ ${ln.sash_max_h || (ln.category === "awning" ? 43.31 : 59.06)}"`}</td>
                  <td className="px-2 text-right font-mono-num text-slate-300">{money(ln.crate_rate)}</td>
                  {actions(ln, i)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {lines && lines.length > 0 && <CategoryDefaults version={version} />}
      {edit && <LineEditor line={edit} types={types} onClose={() => setEdit(null)} onSaved={refresh} />}
      {importing && <SupplierImportDialog types={types} onClose={() => setImporting(false)} onDone={refresh} />}
    </section>
  );
}
